import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AiCvFAQ from "../components/AiCvFAQ";
import GoogleReviews from "../components/GoogleReviews";
import AiComparison from "../components/AiComparison";
import CvSamples, { SAMPLES, TEMPLATES } from "../components/CvSamples";
import CVPreview from "../components/CVPreview";
import CareerReport from "../components/CareerReport";
import { usePageMeta, ORG_JSON_LD, productJsonLd } from "../lib/usePageMeta";
import {
  Sparkles,
  CheckCircle2,
  BadgeCheck,
  ChevronRight,
  Rocket,
  MessageCircle,
  MapPin,
  ShieldCheck,
  LayoutTemplate,
  Users,
  PenLine,
  ListChecks,
  GraduationCap,
  Briefcase,
  Repeat,
  Crown,
  UserPlus,
  Download,
  Send,
} from "lucide-react";

// SEO — CV Maker + Online AI CV Builder as primary keywords in the title;
// broader keyword set (AI CV maker, AI CV builder, CV builder, CV creator,
// AI CV writer, ATS CV builder, resume builder, best CV app) captured in
// meta keywords for Google Ads landing page experience.
const META = {
  seo: {
    title:
      "AI CV Maker & CV Builder · Best UK CV App · Build a CV Online for £9.99 · CV Folks",
    description:
      "AI CV Maker and CV Builder built by professional UK CV writers. Create an ATS-friendly CV online in minutes — AI CV writer, CV creator and resume builder for just £9.99. The best CV app in the UK.",
    keywords:
      "cv maker, cv builder, cv maker online, ai cv writer, best cv app, ats cv builder, cv creator, resume builder, ai cv maker, ai cv builder, build a cv online, create a cv online",
  },
  jsonLd: [
    ORG_JSON_LD,
    productJsonLd({
      id: "ai-cv-builder",
      name: "AI CV Maker & CV Builder — Online CV Creator by CV Folks",
      price: 9.99,
      description:
        "AI CV Maker & CV Builder built by professional UK CV writers. Create an ATS-friendly CV online in minutes with our AI CV writer, CV creator and resume builder for just £9.99.",
    }),
  ],
};

/**
 * Dynamic Keyword Insertion (DKI) — Google Ads final URL support.
 *
 * Append `?kw=cv+builder` (or any whitelisted keyword) to your Ads final URLs
 * and this page swaps the hero H1 keyword, eyebrow, sub-copy and CTA button
 * to match. Only the whitelisted keywords below are honoured — any unknown /
 * missing `kw` falls back to the default hero. All variants keep "£9.99" and
 * "Professional UK CV Writers" for brand + E-E-A-T signal.
 *
 * H1 always renders as: <highlighted>{lead}</highlighted> {accent}.
 */
const DKI_VARIANTS = {
  "cv maker": {
    lead: "CV Maker",
    accent: "built by Professional UK CV Writers",
    eyebrow: "£9.99 · Online CV Maker · UK-based",
    sub: "The £9.99 CV Maker built by UK CV writers — ATS-friendly, recruiter-focused and ready in under 5 minutes.",
    cta: "Build My CV — £9.99",
  },
  "cv builder": {
    lead: "CV Builder",
    accent: "built by Professional UK CV Writers",
    eyebrow: "£9.99 · The UK's smarter CV Builder",
    sub: "A CV Builder guided by professional CV writing methodology — not generic AI prompts. £9.99, one-off, ATS-friendly and yours in minutes.",
    cta: "Start My CV Builder — £9.99",
  },
  "cv maker online": {
    lead: "CV Maker Online",
    accent: "· Built by UK CV Writers",
    eyebrow: "£9.99 · Online CV Maker · UK-based",
    sub: "Make your CV online in minutes with our £9.99 CV Maker — recruiter-informed, ATS-friendly, downloadable as PDF and Word.",
    cta: "Make My CV Online — £9.99",
  },
  "ai cv writer": {
    lead: "AI CV Writer",
    accent: "built by Professional UK CV Writers",
    eyebrow: "£9.99 · AI CV Writer · UK-based",
    sub: "An AI CV writer trained on the same craft our human writers use with paying clients — recruiter-ready, ATS-friendly, £9.99 one-off.",
    cta: "Try the AI CV Writer — £9.99",
  },
  "best cv app": {
    lead: "The Best CV App",
    accent: "for UK Jobs · Built by Professional CV Writers",
    eyebrow: "£9.99 · The UK's best-rated CV app",
    sub: "The best CV app in the UK — built by professional CV writers, ATS-friendly by design, £9.99 one-off and yours in minutes.",
    cta: "Get the CV App — £9.99",
  },
  "ats cv builder": {
    lead: "ATS CV Builder",
    accent: "built by Professional UK CV Writers",
    eyebrow: "£9.99 · ATS-friendly CV Builder · UK",
    sub: "An ATS CV builder tuned to how UK Applicant Tracking Systems actually read a CV. £9.99 one-off, downloadable in PDF and Word.",
    cta: "Build an ATS CV — £9.99",
  },
  "cv creator": {
    lead: "CV Creator",
    accent: "built by Professional UK CV Writers",
    eyebrow: "£9.99 · Online CV Creator · UK-based",
    sub: "The UK CV creator built on professional CV writing methodology — recruiter-focused, ATS-friendly and only £9.99.",
    cta: "Create My CV — £9.99",
  },
  "resume builder": {
    lead: "Resume Builder",
    accent: "· Built by Professional UK Writers",
    eyebrow: "£9.99 · Resume Builder · UK & international roles",
    sub: "A resume builder tuned for UK and international roles by professional writers — ATS-friendly, downloadable in PDF and Word. £9.99 one-off.",
    cta: "Build My Resume — £9.99",
  },
  "ai cv maker": {
    lead: "AI CV Maker",
    accent: "built by Professional UK CV Writers",
    eyebrow: "£9.99 · One-Time Payment · UK-based",
    sub: "Create a recruiter-ready, ATS-friendly CV using AI guided by professional CV writing methodology — not generic AI prompts.",
    cta: "Build My CV — £9.99",
  },
  "ai cv builder": {
    lead: "AI CV Builder",
    accent: "built by Professional UK CV Writers",
    eyebrow: "£9.99 · AI CV Builder · UK-based",
    sub: "The AI CV Builder built on the same craft our UK writers use with paying clients — ATS-friendly, recruiter-focused, £9.99 one-off.",
    cta: "Build My CV — £9.99",
  },
};

const DEFAULT_HERO = DKI_VARIANTS["ai cv maker"];

// Normalise incoming kw (e.g. "cv+builder", "AI CV Writer", "cv%20maker") to
// a lowercase whitelist key. Returns null if not whitelisted.
function normaliseKw(raw) {
  if (!raw) return null;
  const cleaned = String(raw)
    .replace(/\+/g, " ")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return Object.prototype.hasOwnProperty.call(DKI_VARIANTS, cleaned)
    ? cleaned
    : null;
}

// ---------- Conversion constants ----------
const TRUST_BADGES = [
  "Built for UK Jobs",
  "Created by Professional CV Writers",
  "ATS-Friendly Structure",
  "Recruiter-Informed Methodology",
  "Professional Templates",
  "UK English",
  "PDF & Word Download",
  "One-Time Payment",
];

const WHY_CVFOLKS = [
  {
    icon: PenLine,
    title: "Professional CV writing expertise",
    text: "Built by experienced UK CV writers — the same craft behind our professional writing service.",
  },
  {
    icon: Users,
    title: "Recruiter-informed methodology",
    text: "Every section follows what UK recruiters actually shortlist on.",
  },
  {
    icon: ShieldCheck,
    title: "ATS optimisation principles",
    text: "Structured to pass Applicant Tracking Systems cleanly — no parsing traps.",
  },
  {
    icon: BadgeCheck,
    title: "Professional UK CV standards",
    text: "UK English, UK conventions and UK employer expectations throughout.",
  },
  {
    icon: ListChecks,
    title: "Structured career guidance",
    text: "A guided, step-by-step process — no blank pages, no prompt engineering.",
  },
  {
    icon: LayoutTemplate,
    title: "Premium templates",
    text: "Three professional designs following modern UK CV standards.",
  },
];

const WHO_FOR = [
  { icon: GraduationCap, title: "Graduates", text: "Stand out with limited experience" },
  { icon: Briefcase, title: "Professionals", text: "Present your track record with impact" },
  { icon: Repeat, title: "Career Changers", text: "Reframe your skills for a new path" },
  { icon: Users, title: "Managers", text: "Show leadership, not just duties" },
  { icon: Crown, title: "Executives", text: "Senior positioning done properly" },
  { icon: MapPin, title: "UK Job Seekers", text: "Built for the UK job market" },
];

const HOW_IT_WORKS = [
  { icon: UserPlus, title: "Create Account" },
  { icon: ListChecks, title: "Answer Guided Questions" },
  { icon: Sparkles, title: "Generate Your CV" },
  { icon: PenLine, title: "Edit & Improve" },
  { icon: Download, title: "Download PDF & Word" },
  { icon: Send, title: "Apply with Confidence" },
];

const PRICING_INCLUDES = [
  "AI CV Maker",
  "Professional Templates",
  "Unlimited Editing",
  "AI Cover Letter",
  "PDF Download",
  "Word Download",
  "Sarah AI Career Consultant",
  "Secure Dashboard",
];

// Static showcase report for the landing page (real reports are generated
// per-CV by the backend — see services/career_report.py).
const SAMPLE_REPORT = {
  ats_compatibility: 92,
  recruiter_readiness: 88,
  professional_structure: 90,
  achievement_strength: 84,
  keyword_optimisation: 87,
  overall_score: 89,
  headline:
    "A strong, recruiter-ready CV with clear achievements — a few keyword tweaks would make it even sharper.",
  recommendations: [
    "Add a measurable outcome to your second Monzo bullet — recruiters shortlist on numbers.",
    "Mirror the exact skill keywords from your target job adverts in Core Skills.",
    "Add one leadership signal (mentoring, ownership, delivery) to strengthen seniority.",
  ],
};

const scrollToSamples = () => {
  document
    .getElementById("cv-samples")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
};

// ---------- Hero visual — real professional sample (Classic template) ----------
function SampleCV() {
  const template = TEMPLATES.find((t) => t.id === "classic");
  return (
    <div
      data-testid="ai-cv-maker-sample-cv"
      className="group relative w-full max-w-[460px] mx-auto"
    >
      {/* Floating ATS score badge */}
      <div className="absolute -top-4 -right-4 z-20 h-20 w-20 rounded-full bg-white shadow-2xl border border-brand/25 flex flex-col items-center justify-center rotate-6 group-hover:rotate-0 transition-transform duration-500">
        <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
          ATS Score
        </span>
        <span className="font-serif text-2xl font-bold text-brand leading-none mt-0.5">
          96
        </span>
        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
      </div>

      {/* Floating "AI writing" chip */}
      <div className="absolute -left-4 top-24 z-20 hidden sm:flex items-center gap-2 rounded-full bg-foreground text-background px-3 py-1.5 shadow-xl text-[11px] font-semibold -rotate-3 group-hover:rotate-0 transition-transform duration-500">
        <Sparkles className="h-3 w-3 text-brand" /> AI just wrote this
      </div>

      {/* Real sample CV, clipped like a page with a fade at the bottom */}
      <div className="relative h-[620px] rounded-2xl shadow-2xl border border-border overflow-hidden bg-white transition-transform duration-500 group-hover:-translate-y-1 group-hover:shadow-brand/20">
        <div
          className="origin-top-left"
          style={{ transform: "scale(0.72)", width: "139%" }}
        >
          <CVPreview
            cv={SAMPLES.classic}
            template={template}
            className="!rounded-none !border-0 !shadow-none"
          />
        </div>

        {/* Bottom "generated by" ribbon */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white via-white/95 to-transparent px-5 pt-10 pb-3 flex items-center justify-between text-[10px] text-foreground/55">
          <span className="inline-flex items-center gap-1">
            <Sparkles className="h-2.5 w-2.5 text-brand" /> Built with CV Folks AI
          </span>
          <span>Classic · ATS-friendly</span>
        </div>
      </div>
    </div>
  );
}

export default function AiCvMakerPage() {
  usePageMeta(META);
  const [searchParams] = useSearchParams();
  const hero = useMemo(() => {
    const kw = normaliseKw(searchParams.get("kw"));
    return kw ? DKI_VARIANTS[kw] : DEFAULT_HERO;
  }, [searchParams]);

  return (
    <>
      <Navbar />
      <main data-testid="ai-cv-maker-page" className="pt-24 md:pt-32 bg-background overflow-hidden">
        {/* ================= HERO ================= */}
        <section className="relative">
          {/* Soft glow backgrounds */}
          <div className="pointer-events-none absolute -top-40 -left-24 h-96 w-96 rounded-full bg-brand/10 blur-3xl" />
          <div className="pointer-events-none absolute top-20 -right-24 h-96 w-96 rounded-full bg-brand/10 blur-3xl" />

          <div className="relative max-w-6xl mx-auto px-6 md:px-10 pb-16 md:pb-24 grid md:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="md:col-span-7 rise-in">
              <div
                data-testid="ai-cv-maker-hero-eyebrow"
                className="inline-flex items-center gap-2 rounded-full bg-brand/10 border border-brand/20 px-3 py-1.5 text-[11px] font-bold tracking-widest uppercase text-brand"
              >
                <Sparkles className="h-3 w-3" /> {hero.eyebrow}
              </div>
              <h1
                data-testid="ai-cv-maker-hero-h1"
                className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold mt-5 leading-[1.02]"
              >
                <span className="relative inline-block">
                  <span className="relative z-10 text-brand">{hero.lead}</span>
                  <span className="absolute inset-x-0 bottom-1 h-3 bg-brand/25 -z-0 rounded-sm" />
                </span>{" "}
                {hero.accent}
              </h1>
              <p className="text-base md:text-lg text-foreground/70 mt-5 max-w-xl leading-relaxed">
                {hero.sub}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/ai-cv-builder"
                  data-testid="ai-cv-maker-cta"
                  className="btn-primary inline-flex items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold shadow-lg shadow-brand/20 hover:shadow-brand/40 hover:-translate-y-0.5 transition"
                >
                  <Sparkles className="h-4 w-4" /> {hero.cta}
                </Link>
                <button
                  type="button"
                  onClick={scrollToSamples}
                  data-testid="ai-cv-maker-view-samples"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-4 text-sm font-semibold hover:border-brand hover:text-brand transition"
                >
                  View CV Samples <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Trust badges */}
              <div
                data-testid="ai-cv-maker-trust-badges"
                className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2"
              >
                {TRUST_BADGES.map((b) => (
                  <span
                    key={b}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-card border border-border px-2.5 py-2 text-[11px] font-semibold text-foreground/80 leading-tight"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 text-brand shrink-0" />
                    {b}
                  </span>
                ))}
              </div>
            </div>

            <div className="md:col-span-5 relative rise-in">
              <SampleCV />
            </div>
          </div>
        </section>

        {/* ================= WHY CV FOLKS AI? ================= */}
        <section
          data-testid="ai-cv-maker-different"
          className="relative bg-muted/30 py-20 md:py-28 border-y border-border"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <p className="label-eyebrow">Why CV Folks AI?</p>
              <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
                Most AI tools generate text.{" "}
                <span className="text-brand">Ours follows a professional CV writing process.</span>
              </h2>
              <p className="mt-5 text-foreground/70 leading-relaxed">
                Most AI CV builders simply generate text. CV Folks AI is
                different — you follow the same professional CV writing process
                our UK writers use with paying clients, guided step by step,
                rather than relying on generic AI output.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/ai-cv-builder"
                  data-testid="ai-cv-maker-different-cta"
                  className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
                >
                  <Sparkles className="h-4 w-4" /> Build My CV — £9.99
                </Link>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event("sarah:open"))}
                  data-testid="ai-cv-maker-chat-sarah"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3.5 text-sm font-semibold hover:border-brand hover:text-brand transition"
                >
                  <MessageCircle className="h-4 w-4" /> Ask Sarah
                </button>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
                <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand">
                  What CV Folks AI combines
                </p>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {WHY_CVFOLKS.map((b) => (
                    <div
                      key={b.title}
                      data-testid={`built-by-item-${b.title.replace(/\s+/g, "-").toLowerCase()}`}
                      className="group flex items-start gap-3 rounded-xl bg-background border border-border px-4 py-3.5 hover:border-brand/40 hover:bg-brand/5 transition"
                    >
                      <span className="h-8 w-8 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0 group-hover:bg-brand group-hover:text-white transition">
                        <b.icon className="h-4 w-4" />
                      </span>
                      <div>
                        <p className="text-[13px] font-bold leading-snug">{b.title}</p>
                        <p className="text-xs text-foreground/65 mt-1 leading-relaxed">
                          {b.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-border grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="font-serif text-2xl md:text-3xl font-bold text-brand">
                      &lt; 5 min
                    </p>
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1 font-bold">
                      Avg. build time
                    </p>
                  </div>
                  <div className="border-x border-border">
                    <p className="font-serif text-2xl md:text-3xl font-bold text-brand">
                      £9.99
                    </p>
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1 font-bold">
                      One-time payment
                    </p>
                  </div>
                  <div>
                    <p className="font-serif text-2xl md:text-3xl font-bold text-brand">
                      100%
                    </p>
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1 font-bold">
                      ATS-friendly
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================= WHY PAY £9.99? (comparison) ================= */}
        <AiComparison />

        {/* ================= WHO IS THIS FOR? ================= */}
        <section
          data-testid="ai-cv-maker-who-for"
          className="py-20 md:py-28"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <p className="label-eyebrow">Who is this for?</p>
              <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
                Perfect for every stage of your career.
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {WHO_FOR.map((w) => (
                <div
                  key={w.title}
                  data-testid={`who-for-${w.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group rounded-2xl border border-border bg-card p-5 md:p-6 text-center hover:border-brand/40 hover:shadow-lg hover:-translate-y-0.5 transition"
                >
                  <span className="mx-auto h-11 w-11 rounded-xl bg-brand/10 text-brand flex items-center justify-center group-hover:bg-brand group-hover:text-white transition">
                    <w.icon className="h-5 w-5" />
                  </span>
                  <h3 className="font-serif text-lg font-bold mt-4">{w.title}</h3>
                  <p className="text-xs text-foreground/65 mt-1.5">{w.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= HOW IT WORKS ================= */}
        <section
          data-testid="ai-cv-maker-how-it-works"
          className="relative bg-muted/30 py-20 md:py-28 border-y border-border"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <p className="label-eyebrow">How it works</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold mt-3 leading-tight">
                From account to application in six simple steps.
              </h2>
            </div>
            <div className="flex flex-col md:flex-row md:flex-wrap items-center justify-center gap-2 md:gap-0">
              {HOW_IT_WORKS.map((s, i) => (
                <div key={s.title} className="flex flex-col md:flex-row items-center">
                  <div
                    data-testid={`how-step-${i + 1}`}
                    className="flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 hover:border-brand/50 hover:shadow-md transition w-64 md:w-auto"
                  >
                    <span className="h-9 w-9 rounded-full bg-brand text-white flex items-center justify-center shrink-0">
                      <s.icon className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                        Step {i + 1}
                      </p>
                      <p className="text-sm font-bold leading-tight">{s.title}</p>
                    </div>
                  </div>
                  {i < HOW_IT_WORKS.length - 1 && (
                    <ChevronRight className="h-5 w-5 text-brand rotate-90 md:rotate-0 my-1 md:my-0 md:mx-1.5 shrink-0" />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link
                to="/ai-cv-builder"
                data-testid="how-it-works-cta"
                className="btn-primary inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
              >
                <Sparkles className="h-4 w-4" /> Build My CV — £9.99
              </Link>
            </div>
          </div>
        </section>

        {/* ================= CAREER QUALITY REPORT ================= */}
        <section
          data-testid="ai-cv-maker-career-report"
          className="py-20 md:py-28"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-6 min-w-0">
              <p className="label-eyebrow">Career Quality Report</p>
              <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
                Your CV, scored like a{" "}
                <span className="text-brand">UK recruiter would.</span>
              </h2>
              <p className="mt-5 text-foreground/70 leading-relaxed">
                Every CV you generate comes with a Career Quality Report — not
                just a finished document. See how your CV scores on ATS
                compatibility, recruiter readiness, professional structure,
                achievement strength and keyword optimisation, plus practical
                recommendations to make it even stronger.
              </p>
              <ul className="mt-6 space-y-2.5 text-sm">
                {[
                  "ATS Compatibility",
                  "Recruiter Readiness",
                  "Professional Structure",
                  "Achievement Strength",
                  "Keyword Optimisation",
                  "Overall CV Score + practical recommendations",
                ].map((x) => (
                  <li key={x} className="flex items-start gap-2 text-foreground/85">
                    <CheckCircle2 className="h-4 w-4 text-brand mt-0.5 shrink-0" />
                    <span>{x}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-foreground/60">
                No other £9.99 CV builder shows you how a recruiter would judge
                your CV — and how to improve it.
              </p>
            </div>
            <div className="lg:col-span-6 min-w-0">
              <CareerReport report={SAMPLE_REPORT} />
              <p className="text-center text-xs text-muted-foreground mt-3">
                Example report — yours is generated for your specific CV and target role.
              </p>
            </div>
          </div>
        </section>

        {/* ================= CV SAMPLES (templates) ================= */}
        <CvSamples />

        {/* ================= PRICING ================= */}
        <section
          data-testid="ai-cv-maker-large-cta"
          className="max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-28"
        >
          <div className="relative overflow-hidden rounded-[2.5rem] border border-brand/30 bg-gradient-to-br from-brand/5 via-background to-brand/10 p-8 md:p-14">
            <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-brand/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-brand/10 blur-3xl" />
            <div className="relative grid md:grid-cols-12 gap-10 items-center">
              <div className="md:col-span-7">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand/10 text-brand text-xs font-semibold tracking-wide uppercase">
                  <Rocket className="h-3.5 w-3.5" /> One-Time Payment · No Subscription
                </div>
                <h2 className="font-serif text-3xl md:text-5xl font-bold mt-4 leading-[1.05]">
                  Everything included for{" "}
                  <span className="text-brand">£9.99.</span>
                </h2>
                <p className="text-base md:text-lg text-foreground/70 mt-5">
                  One payment. The complete professional CV toolkit — built by
                  UK CV writers, designed for the UK job market.
                </p>

                <ul className="mt-7 grid sm:grid-cols-2 gap-2.5 text-sm">
                  {PRICING_INCLUDES.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-foreground/85">
                      <CheckCircle2 className="h-4 w-4 text-brand mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-9 flex flex-wrap items-center gap-4">
                  <Link
                    to="/ai-cv-builder"
                    data-testid="ai-cv-maker-large-cta-primary"
                    className="btn-primary inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-semibold shadow-lg shadow-brand/20 hover:shadow-brand/40 hover:-translate-y-0.5 transition"
                  >
                    <Sparkles className="h-5 w-5" /> Build My CV — £9.99
                  </Link>
                  <Link
                    to="/pricing"
                    data-testid="ai-cv-maker-large-cta-secondary"
                    className="inline-flex items-center gap-2 rounded-full border border-foreground bg-foreground text-background hover:bg-foreground/90 px-6 py-4 text-sm font-semibold transition"
                  >
                    Compare all packages <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
                <p className="text-xs text-foreground/60 mt-4">
                  Secure payment via PayPal · UK company · No hidden fees · £9.99 credits toward any writing package
                </p>
              </div>

              <div className="md:col-span-5">
                <div className="relative rounded-3xl border border-border bg-card p-7 shadow-xl hover:-translate-y-1 transition">
                  <div className="flex items-center gap-2 text-brand text-xs font-bold uppercase tracking-[0.18em]">
                    <BadgeCheck className="h-4 w-4" /> AI CV Maker
                  </div>
                  <p className="mt-3 font-serif text-6xl font-bold leading-none">
                    £9.99
                  </p>
                  <p className="text-sm font-semibold mt-1.5">
                    One-Time Payment · <span className="text-brand">No Subscription</span>
                  </p>

                  <div className="mt-6 pt-6 border-t border-border space-y-3 text-sm">
                    {[
                      "AI CV Maker + templates",
                      "AI cover letter",
                      "PDF + Word download",
                      "Unlimited editing",
                      "Career Quality Report",
                    ].map((row) => (
                      <div key={row} className="flex items-center justify-between">
                        <span className="text-foreground/70">{row}</span>
                        <CheckCircle2 className="h-4 w-4 text-brand" />
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/ai-cv-builder"
                    data-testid="ai-cv-maker-price-card-cta"
                    className="btn-primary mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
                  >
                    Build My CV — £9.99 <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <GoogleReviews />
        <AiCvFAQ />

        {/* ================= FINAL CTA ================= */}
        <section
          data-testid="ai-cv-maker-final-cta"
          className="relative overflow-hidden bg-foreground text-background py-20 md:py-28"
        >
          <div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-brand/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
          <div className="relative max-w-3xl mx-auto px-6 md:px-10 text-center">
            <p className="label-eyebrow text-brand">Ready when you are</p>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
              Your Next Interview Starts With a Better CV
            </h2>
            <p className="mt-5 text-base md:text-lg text-background/75 leading-relaxed">
              Create a recruiter-ready CV using AI built by experienced UK CV
              writers and designed specifically for the UK job market.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/ai-cv-builder"
                data-testid="ai-cv-maker-final-cta-btn"
                className="btn-primary inline-flex items-center gap-2 rounded-full px-9 py-4 text-base font-semibold shadow-lg shadow-brand/30 hover:shadow-brand/50 hover:-translate-y-0.5 transition"
              >
                <Sparkles className="h-5 w-5" /> Build My CV — £9.99
              </Link>
            </div>
            <p className="mt-4 text-xs text-background/60">
              One-time payment · No subscription · PDF &amp; Word download
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
